import { useEffect, useMemo, useState } from 'react';

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  GraduationCapIcon,
  LegoIcon,
  MagnifyingGlassIcon,
  MinusIcon,
  TargetIcon,
  XIcon,
} from '@phosphor-icons/react';

import { cn } from '@shared/lib/utils';
import { Button } from '@shared/ui/button';
import { PracticeCard } from '@shared/ui/practice-card';

export type NewPracticeModalStep = 'menu' | 'full' | 'custom' | 'targeted';

type PracticeStep = NewPracticeModalStep;
type SectionId = 'quant' | 'verbal' | 'data';
type Difficulty = 'easy' | 'medium' | 'hard';
type TargetedStep = 'list' | 'settings';

export interface CustomTestSettings {
  sections: {
    id: SectionId;
    label: string;
    topics: string[];
  }[];
  difficulty: Difficulty;
  aiMode: boolean;
  timeLimitEnabled: boolean;
  estimatedQuestions: number;
  estimatedMinutes: number;
}

export interface TargetedPracticeSettings {
  topic: {
    title: string;
    score: number;
  };
  questionCount: number;
  difficulty: Difficulty;
  aiMode: boolean;
  estimatedMinutes: number;
}

interface NewPracticeModalProps {
  isOpen: boolean;
  initialStep?: NewPracticeModalStep;
  onClose: () => void;
  onStartCustomTest?: (settings: CustomTestSettings) => void;
  onStartTargetedPractice?: (settings: TargetedPracticeSettings) => void;
}

const menuItems = [
  {
    id: 'full',
    title: 'Start full exam',
    description: 'Real life GMAT Focus simulation: 64 questions, approx. 2h 15m long.',
    icon: <GraduationCapIcon size={28} weight="duotone" color="#155DFC" />,
  },
  {
    id: 'custom',
    title: 'Create a custom test',
    description: 'Test builder: start with a partial template or build your own.',
    icon: <LegoIcon size={28} weight="duotone" color="#FF8904" />,
  },
  {
    id: 'targeted',
    title: 'Start targeted practice',
    description: "Strengthen your knowledge in the areas you're struggling with.",
    icon: <TargetIcon size={28} weight="duotone" color="#5EA500" />,
  },
] as const satisfies {
  id: PracticeStep;
  title: string;
  description: string;
  icon: JSX.Element;
}[];

const fullExamStats = [
  { value: '3', label: 'Sections' },
  { value: '64', label: 'Questions' },
  { value: '2:15', label: 'Avg. time' },
];

const customTestSections = [
  {
    id: 'quant',
    label: 'Quantitative Reasoning',
    groups: [
      {
        label: 'Algebra',
        topics: [
          'Algebraic expressions and polynomials',
          'Functions',
          'Inequalities',
          'Linear equations',
          'Permutations and combinations',
          'Progressions',
          'Quadratic equations',
        ],
      },
      {
        label: 'Arithmetic',
        topics: [
          'Quadratic equations',
          'Averages',
          'Decimals',
          'Fractions',
          'Mixtures and allegations',
          'Number properties',
          'Percentages',
          'Powers and roots',
          'Probability',
          'Profit, loss, and discounts',
          'Ratios and proportions',
          'Simple and compound interest',
          'Speed, distance, and time',
          'Statistics',
          'Work and time',
        ],
      },
    ],
  },
  {
    id: 'verbal',
    label: 'Verbal Reasoning',
    groups: [
      {
        label: 'Reading',
        topics: [
          'Main idea and tone',
          'Inference and logic',
          'Critical reasoning',
          'Strengthen or weaken',
          'Assumptions',
          'Paragraph structure',
        ],
      },
      {
        label: 'Sentence Correction',
        topics: [
          'Grammar basics',
          'Parallelism',
          'Modifiers',
          'Subject-verb agreement',
          'Pronouns',
          'Verb tenses',
        ],
      },
    ],
  },
  {
    id: 'data',
    label: 'Data Insights',
    groups: [
      {
        label: 'Charts',
        topics: ['Data sufficiency', 'Table analysis', 'Graph interpretation', 'Two-part analysis'],
      },
      {
        label: 'Logic',
        topics: [
          'Multi-source reasoning',
          'Integrated reasoning',
          'Probability and stats',
          'Scenario analysis',
        ],
      },
    ],
  },
] as const satisfies {
  id: SectionId;
  label: string;
  groups: {
    label: string;
    topics: string[];
  }[];
}[];

const getSectionTopics = (sectionId: SectionId): string[] => {
  const section = customTestSections.find((item) => item.id === sectionId);

  if (!section) {
    return [];
  }

  return section.groups.flatMap((group) => [...group.topics]);
};

const targetedTopics = [
  { score: 49, title: 'Probability' },
  { score: 66, title: 'Algebraic expressions and polynomials' },
  { score: 73, title: 'Decimals' },
  { score: 78, title: 'Number properties' },
  { score: 79, title: 'Linear equations' },
  { score: 83, title: 'Speed, distance, and time' },
  { score: 83, title: 'Ratios and proportions' },
  { score: 84, title: 'Quadratic equations' },
  { score: 85, title: 'Powers and roots' },
  { score: 85, title: 'Profit, loss, and discounts' },
  { score: 87, title: 'Inequalities' },
];

const getScoreColor = (score: number) => {
  if (score >= 80) return 'text-lime-600';
  if (score >= 70) return 'text-emerald-600';
  if (score >= 60) return 'text-amber-500';
  return 'text-rose-500';
};

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours)}:${String(mins).padStart(2, '0')}`;
};

export const NewPracticeModal = ({
  isOpen,
  initialStep = 'menu',
  onClose,
  onStartCustomTest,
  onStartTargetedPractice,
}: NewPracticeModalProps) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [step, setStep] = useState<PracticeStep>('menu');
  const [selectedSections, setSelectedSections] = useState<SectionId[]>(['quant']);
  const [selectedTopicsBySection, setSelectedTopicsBySection] = useState<
    Partial<Record<SectionId, string[]>>
  >({
    quant: getSectionTopics('quant'),
  });
  const [aiModeEnabled, setAiModeEnabled] = useState(false);
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(false);
  const [targetedStep, setTargetedStep] = useState<TargetedStep>('list');
  const [targetedQuery, setTargetedQuery] = useState('');
  const [selectedTarget, setSelectedTarget] = useState<(typeof targetedTopics)[number] | null>(
    null
  );
  const [targetedQuestions, setTargetedQuestions] = useState(31);
  const [targetedDifficulty, setTargetedDifficulty] = useState<Difficulty>('medium');
  const [targetedAiModeEnabled, setTargetedAiModeEnabled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setStep(initialStep);
      setSelectedSections(['quant']);
      setSelectedTopicsBySection({ quant: getSectionTopics('quant') });
      setAiModeEnabled(false);
      setDifficulty('medium');
      setTimeLimitEnabled(false);
      setTargetedStep('list');
      setTargetedQuery('');
      setSelectedTarget(null);
      setTargetedQuestions(31);
      setTargetedDifficulty('medium');
      setTargetedAiModeEnabled(false);
    } else {
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  }, [initialStep, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const modalSize = useMemo(() => {
    switch (step) {
      case 'custom':
        return 'max-w-[1040px]';
      case 'targeted':
        return 'max-w-[680px]';
      case 'full':
        return 'max-w-[460px]';
      default:
        return 'max-w-[420px]';
    }
  }, [step]);

  const totalSelectedTopics = useMemo(() => {
    return Object.values(selectedTopicsBySection).reduce((acc, topics) => acc + topics.length, 0);
  }, [selectedTopicsBySection]);

  const estimatedQuestions = useMemo(() => {
    return Math.max(0, totalSelectedTopics * 2);
  }, [totalSelectedTopics]);

  const estimatedMinutes = useMemo(() => {
    const difficultyMultiplier = difficulty === 'easy' ? 2.2 : difficulty === 'hard' ? 3 : 2.6;
    const baseMinutes = Math.round(estimatedQuestions * difficultyMultiplier);
    return Math.max(15, baseMinutes);
  }, [difficulty, estimatedQuestions]);

  const customTestSettings = useMemo<CustomTestSettings>(() => {
    return {
      sections: customTestSections
        .filter((section) => selectedSections.includes(section.id))
        .map((section) => ({
          id: section.id,
          label: section.label,
          topics: selectedTopicsBySection[section.id] ?? [],
        })),
      difficulty,
      aiMode: aiModeEnabled,
      timeLimitEnabled,
      estimatedQuestions,
      estimatedMinutes,
    };
  }, [
    aiModeEnabled,
    difficulty,
    estimatedMinutes,
    estimatedQuestions,
    selectedSections,
    selectedTopicsBySection,
    timeLimitEnabled,
  ]);

  const handleToggleSection = (sectionId: SectionId) => {
    setSelectedSections((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((item) => item !== sectionId);
      }
      return [...prev, sectionId];
    });

    setSelectedTopicsBySection((prev) => {
      if (prev[sectionId]) {
        const { [sectionId]: removedTopics, ...next } = prev;
        void removedTopics;
        return next;
      }
      return {
        ...prev,
        [sectionId]: getSectionTopics(sectionId),
      };
    });
  };

  const handleToggleTopic = (sectionId: SectionId, topic: string) => {
    setSelectedTopicsBySection((prev) => {
      const currentTopics = prev[sectionId] ?? [];
      const hasTopic = currentTopics.includes(topic);
      const nextTopics = hasTopic
        ? currentTopics.filter((item) => item !== topic)
        : [...currentTopics, topic];
      return {
        ...prev,
        [sectionId]: nextTopics,
      };
    });
  };

  const handleStartCustomTest = () => {
    onStartCustomTest?.(customTestSettings);
    onClose();
  };

  const filteredTargetedTopics = useMemo(() => {
    const query = targetedQuery.trim().toLowerCase();
    if (!query) return targetedTopics;
    return targetedTopics.filter((topic) => topic.title.toLowerCase().includes(query));
  }, [targetedQuery]);

  const targetedEstimatedMinutes = useMemo(() => {
    const multiplier =
      targetedDifficulty === 'easy' ? 1.8 : targetedDifficulty === 'hard' ? 2.6 : 2.2;
    return Math.max(10, Math.round(targetedQuestions * multiplier));
  }, [targetedDifficulty, targetedQuestions]);

  const handleStartTargetedPractice = () => {
    if (!selectedTarget) return;
    onStartTargetedPractice?.({
      topic: selectedTarget,
      questionCount: targetedQuestions,
      difficulty: targetedDifficulty,
      aiMode: targetedAiModeEnabled,
      estimatedMinutes: targetedEstimatedMinutes,
    });
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-100 flex items-center justify-center p-4 transition-opacity duration-300',
        isOpen ? 'opacity-100' : 'opacity-0'
      )}
    >
      <div className="absolute inset-0 bg-black/40 transition-all duration-300" onClick={onClose} />
      <div
        className={cn(
          `
            relative z-10 flex w-full flex-col rounded-3xl bg-white shadow-2xl transition-all
            duration-300
          `,
          'max-h-[90vh] overflow-hidden',
          modalSize,
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        )}
      >
        {step === 'menu' && (
          <>
            <div className="flex items-center justify-between px-6 py-4">
              <div className="w-8" />
              <h2 className="text-base font-medium text-foreground">New practice</h2>
              <button
                type="button"
                onClick={onClose}
                className="
                  rounded-full p-1.5 text-muted-foreground transition-colors
                  hover:bg-accent hover:text-foreground
                "
                aria-label="Close"
              >
                <XIcon className="size-4" />
              </button>
            </div>
            <div className="px-6 pb-6">
              <div className="flex flex-col gap-3">
                {menuItems.map((item) => (
                  <PracticeCard
                    key={item.id}
                    title={item.title}
                    description={item.description}
                    icon={item.icon}
                    onClick={() => {
                      setStep(item.id);
                    }}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {step === 'full' && (
          <>
            <div className="flex items-center justify-between px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  setStep('menu');
                }}
                className="
                  rounded-full p-1.5 text-muted-foreground transition-colors
                  hover:bg-accent hover:text-foreground
                "
                aria-label="Back"
              >
                <ArrowLeftIcon className="size-4" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="
                  rounded-full p-1.5 text-muted-foreground transition-colors
                  hover:bg-accent hover:text-foreground
                "
                aria-label="Close"
              >
                <XIcon className="size-4" />
              </button>
            </div>
            <div className="flex flex-col items-center px-8 pb-6 text-center">
              <GraduationCapIcon size={44} weight="duotone" color="#155DFC" />
              <h2 className="mt-3 text-xl font-medium text-foreground">Full exam</h2>
              <p className="mt-1 text-sm text-muted-foreground">Real life GMAT Focus simulation</p>
              <div className="mt-5 grid w-full grid-cols-3 gap-3">
                {fullExamStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl border border-border/60 bg-card px-3 py-4 shadow-sm"
                  >
                    <div className="text-xl font-medium text-foreground">{stat.value}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
              <Button className="mt-6 w-full" size="lg">
                Start Full Exam
              </Button>
              <Button className="mt-3 w-full" variant="outline" size="lg" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </>
        )}

        {step === 'custom' && (
          <>
            <div className="flex items-center justify-between px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  setStep('menu');
                }}
                className="
                  rounded-full p-1.5 text-muted-foreground transition-colors
                  hover:bg-accent hover:text-foreground
                "
                aria-label="Back"
              >
                <ArrowLeftIcon className="size-4" />
              </button>
              <div
                className="
                flex flex-1 items-center justify-center gap-2 text-sm font-medium text-foreground
              "
              >
                <LegoIcon size={20} weight="duotone" color="#FF8904" />
                Custom test builder
              </div>
              <button
                type="button"
                onClick={onClose}
                className="
                  rounded-full p-1.5 text-muted-foreground transition-colors
                  hover:bg-accent hover:text-foreground
                "
                aria-label="Close"
              >
                <XIcon className="size-4" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
              <div
                className="
                grid gap-6
                lg:grid-cols-[260px_1fr]
              "
              >
                <div className="space-y-6">
                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase">
                      Sections
                    </div>
                    <div className="mt-3 space-y-1.5">
                      {customTestSections.map((section) => {
                        const totalTopics = getSectionTopics(section.id).length;
                        const selectedTopics = selectedTopicsBySection[section.id]?.length ?? 0;
                        const isSelected = selectedSections.includes(section.id);
                        const isPartial = selectedTopics > 0 && selectedTopics < totalTopics;
                        const isChecked = selectedTopics === totalTopics && isSelected;

                        return (
                          <button
                            key={section.id}
                            type="button"
                            onClick={() => {
                              handleToggleSection(section.id);
                            }}
                            className={cn(
                              `
                                flex w-full items-center justify-between rounded-xl px-3 py-2
                                text-left text-sm transition
                              `,
                              isSelected
                                ? 'bg-background-4 text-foreground'
                                : `
                                  text-muted-foreground
                                  hover:bg-background-0
                                `
                            )}
                          >
                            <span>{section.label}</span>
                            <span
                              className={cn(
                                'flex size-5 items-center justify-center rounded-md border',
                                isChecked && 'border-primary bg-primary text-white',
                                isPartial && 'border-primary text-primary',
                                !isChecked && !isPartial && 'border-border/60 text-muted-foreground'
                              )}
                            >
                              {isChecked && <CheckIcon className="size-3" />}
                              {isPartial && <MinusIcon className="size-3" />}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase">
                      Difficulty
                    </div>
                    <div
                      className="
                      mt-3 flex items-center justify-between text-sm text-muted-foreground
                    "
                    >
                      <span>Adaptive AI mode</span>
                      <button
                        type="button"
                        onClick={() => {
                          setAiModeEnabled((prev) => !prev);
                        }}
                        className={cn(
                          `
                            relative flex h-5 w-9 items-center rounded-full border px-0.5
                            transition-colors
                          `,
                          aiModeEnabled
                            ? 'border-primary bg-primary/15'
                            : 'border-border/60 bg-background-0'
                        )}
                      >
                        <span
                          className={cn(
                            'size-4 rounded-full transition-transform duration-200',
                            aiModeEnabled
                              ? 'translate-x-4 bg-primary'
                              : 'translate-x-0 bg-background-3'
                          )}
                        />
                      </button>
                    </div>
                    <div className="mt-3 flex gap-2">
                      {(['easy', 'medium', 'hard'] as const).map((level) => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => {
                            setDifficulty(level);
                          }}
                          className={cn(
                            'rounded-full border px-3 py-1 text-xs font-medium transition',
                            difficulty === level
                              ? 'border-primary bg-primary/10 text-primary'
                              : `
                                border-border/60 text-muted-foreground
                                hover:bg-background-0
                              `
                          )}
                        >
                          {level.charAt(0).toUpperCase() + level.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase">
                      Duration
                    </div>
                    <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                      <div className="flex items-center justify-between">
                        <span>Estimated</span>
                        <span className="text-foreground">{formatDuration(estimatedMinutes)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Time limit</span>
                        <button
                          type="button"
                          onClick={() => {
                            setTimeLimitEnabled((prev) => !prev);
                          }}
                          className={cn(
                            `
                              relative flex h-5 w-9 items-center rounded-full border px-0.5
                              transition-colors
                            `,
                            timeLimitEnabled
                              ? 'border-primary bg-primary/15'
                              : 'border-border/60 bg-background-0'
                          )}
                        >
                          <span
                            className={cn(
                              'size-4 rounded-full transition-transform duration-200',
                              timeLimitEnabled
                                ? 'translate-x-4 bg-primary'
                                : 'translate-x-0 bg-background-3'
                            )}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {customTestSections
                    .filter((section) => selectedSections.includes(section.id))
                    .map((section) => (
                      <div key={section.id} className="space-y-4">
                        {section.groups.map((group) => (
                          <div
                            key={group.label}
                            className="rounded-2xl border border-border/60 bg-card p-4"
                          >
                            <div
                              className="
                              flex items-center gap-2 text-xs font-medium text-muted-foreground
                              uppercase
                            "
                            >
                              <span
                                className="
                                flex size-5 items-center justify-center rounded-md border
                                border-border/60 bg-background-0 text-primary
                              "
                              >
                                <CheckIcon className="size-3" />
                              </span>
                              {group.label}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {group.topics.map((topic) => {
                                const isSelected =
                                  selectedTopicsBySection[section.id]?.includes(topic) ?? false;
                                return (
                                  <button
                                    key={topic}
                                    type="button"
                                    onClick={() => {
                                      handleToggleTopic(section.id, topic);
                                    }}
                                    className={cn(
                                      'rounded-full border px-3 py-1 text-xs font-medium transition',
                                      isSelected
                                        ? 'border-primary/80 bg-primary/10 text-primary'
                                        : `
                                          border-border/60 text-muted-foreground
                                          hover:bg-background-0
                                        `
                                    )}
                                  >
                                    {topic}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  {selectedSections.length === 0 && (
                    <div
                      className="
                      rounded-2xl border border-dashed border-border/60 bg-background-0 p-6 text-sm
                      text-muted-foreground
                    "
                    >
                      Select a section to see available topics.
                    </div>
                  )}
                </div>
              </div>

              <div
                className="
                mt-6 flex flex-col items-center justify-between gap-4 border-t border-border/60 pt-4
                text-sm text-muted-foreground
                sm:flex-row
              "
              >
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-foreground">
                    {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                  </span>
                  <span>Difficulty</span>
                  <span className="text-foreground">{estimatedQuestions} Questions</span>
                  <span>
                    {formatDuration(estimatedMinutes)} Time estimate{' '}
                    {timeLimitEnabled ? '(time limit)' : '(no limit)'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleStartCustomTest}>
                    Start Custom Test
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {step === 'targeted' && (
          <>
            {targetedStep === 'list' && (
              <>
                <div className="flex items-center justify-between px-6 py-4">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('menu');
                    }}
                    className="
                      rounded-full p-1.5 text-muted-foreground transition-colors
                      hover:bg-accent hover:text-foreground
                    "
                    aria-label="Back"
                  >
                    <ArrowLeftIcon className="size-4" />
                  </button>
                  <div
                    className="
                    flex flex-1 items-center justify-center gap-2 text-sm font-medium
                    text-foreground
                  "
                  >
                    <TargetIcon size={20} weight="duotone" color="#5EA500" />
                    Targeted practice
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="
                      rounded-full p-1.5 text-muted-foreground transition-colors
                      hover:bg-accent hover:text-foreground
                    "
                    aria-label="Close"
                  >
                    <XIcon className="size-4" />
                  </button>
                </div>
                <div className="px-6 pb-6">
                  <div
                    className="
                    flex items-center gap-3 rounded-2xl border border-border/60 bg-card px-4 py-3
                    text-sm text-muted-foreground shadow-sm
                  "
                  >
                    <MagnifyingGlassIcon className="size-4" />
                    <input
                      type="text"
                      value={targetedQuery}
                      onChange={(event) => {
                        setTargetedQuery(event.target.value);
                      }}
                      placeholder="Search for a subskill or topic..."
                      className="
                        w-full bg-transparent text-sm text-foreground
                        placeholder:text-muted-foreground
                        focus:outline-none
                      "
                    />
                    <ArrowRightIcon className="size-4 text-muted-foreground" />
                  </div>

                  <div className="mt-4 max-h-100 space-y-3 overflow-y-auto pr-1">
                    {filteredTargetedTopics.length === 0 && (
                      <div
                        className="
                        rounded-2xl border border-dashed border-border/60 bg-background-0 p-6
                        text-sm text-muted-foreground
                      "
                      >
                        No topics found.
                      </div>
                    )}
                    {filteredTargetedTopics.map((topic) => {
                      const isSelected = selectedTarget?.title === topic.title;
                      return (
                        <button
                          key={topic.title}
                          type="button"
                          onClick={() => {
                            setSelectedTarget(topic);
                          }}
                          className={cn(
                            `
                              flex w-full items-center justify-between gap-4 rounded-2xl border px-4
                              py-3 text-left shadow-sm transition
                            `,
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : `
                                border-border/60 bg-card
                                hover:bg-background-0
                              `
                          )}
                        >
                          <div className="flex items-center gap-4">
                            <span className={cn('text-lg font-medium', getScoreColor(topic.score))}>
                              {topic.score}%
                            </span>
                            <span className="text-sm font-medium text-foreground">
                              {topic.title}
                            </span>
                          </div>
                          {isSelected && <CheckIcon className="size-4 text-primary" />}
                        </button>
                      );
                    })}
                  </div>

                  <div
                    className="
                    mt-5 flex flex-col items-center justify-between gap-3 text-sm
                    text-muted-foreground
                    sm:flex-row
                  "
                  >
                    <span>
                      {selectedTarget ? selectedTarget.title : 'Select a topic to continue'}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setTargetedStep('settings');
                        }}
                        disabled={!selectedTarget}
                      >
                        Next Step
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {targetedStep === 'settings' && selectedTarget && (
              <>
                <div className="flex items-center justify-between px-6 py-4">
                  <button
                    type="button"
                    onClick={() => {
                      setTargetedStep('list');
                    }}
                    className="
                      rounded-full p-1.5 text-muted-foreground transition-colors
                      hover:bg-accent hover:text-foreground
                    "
                    aria-label="Back"
                  >
                    <ArrowLeftIcon className="size-4" />
                  </button>
                  <div
                    className="
                    flex flex-1 items-center justify-center gap-2 text-sm font-medium
                    text-foreground
                  "
                  >
                    <TargetIcon size={20} weight="duotone" color="#5EA500" />
                    Targeted practice
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="
                      rounded-full p-1.5 text-muted-foreground transition-colors
                      hover:bg-accent hover:text-foreground
                    "
                    aria-label="Close"
                  >
                    <XIcon className="size-4" />
                  </button>
                </div>
                <div className="px-6 pb-6">
                  <div
                    className="
                    flex items-center justify-between rounded-2xl border border-primary/50
                    bg-primary/5 px-4 py-3 text-sm
                  "
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={cn('text-lg font-medium', getScoreColor(selectedTarget.score))}
                      >
                        {selectedTarget.score}%
                      </span>
                      <span className="font-medium text-foreground">{selectedTarget.title}</span>
                    </div>
                    <CheckIcon className="size-4 text-primary" />
                  </div>

                  <div className="mt-5 space-y-5 text-sm text-muted-foreground">
                    <div className="space-y-3">
                      <div className="text-xs font-medium text-muted-foreground uppercase">
                        Number of questions
                      </div>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min={10}
                          max={60}
                          step={1}
                          value={targetedQuestions}
                          onChange={(event) => {
                            setTargetedQuestions(Number(event.target.value));
                          }}
                          className="w-full accent-primary"
                        />
                        <div
                          className="
                          min-w-11 rounded-lg border border-border/60 bg-background-0 px-3 py-2
                          text-center text-sm text-foreground
                        "
                        >
                          {targetedQuestions}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="text-xs font-medium text-muted-foreground uppercase">
                        Difficulty
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        {(['easy', 'medium', 'hard'] as const).map((level) => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => {
                              setTargetedDifficulty(level);
                            }}
                            className={cn(
                              'rounded-full border px-3 py-1 text-xs font-medium transition',
                              targetedDifficulty === level
                                ? 'border-primary bg-primary/10 text-primary'
                                : `
                                  border-border/60 text-muted-foreground
                                  hover:bg-background-0
                                `
                            )}
                          >
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </button>
                        ))}
                        <div className="ml-auto flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setTargetedAiModeEnabled((prev) => !prev);
                            }}
                            className={cn(
                              `
                                relative flex h-5 w-9 items-center rounded-full border px-0.5
                                transition-colors
                              `,
                              targetedAiModeEnabled
                                ? 'border-primary bg-primary/15'
                                : 'border-border/60 bg-background-0'
                            )}
                          >
                            <span
                              className={cn(
                                'size-4 rounded-full transition-transform duration-200',
                                targetedAiModeEnabled
                                  ? 'translate-x-4 bg-primary'
                                  : 'translate-x-0 bg-background-3'
                              )}
                            />
                          </button>
                          <span className="text-xs">Adaptive AI difficulty</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="
                    mt-6 flex flex-col items-center justify-between gap-3 text-sm
                    text-muted-foreground
                    sm:flex-row
                  "
                  >
                    <span>{formatDuration(targetedEstimatedMinutes)} Time estimate (no limit)</span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleStartTargetedPractice}>
                        Start Practice
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
